import { ErrorMessage } from '@hookform/error-message'
import { useFormContext } from 'react-hook-form'
import { useRef, useEffect, useState, useContext } from 'react'
import { useTranslation } from 'next-i18next'
import PropTypes from 'prop-types'
import RichTextBlock from '../RichTextBlock/RichTextBlock'

// Utils
import convertDataToTranslation from '../../utils/data/convertDataToTranslation'

// Internal components
import FormError from '../FormError/FormError'
import Svg from '../Svg/Svg'
import { UserContext } from '@/root/context/UserContext'

const revealClass = ' is-revealed'
const activeClass = ' is-active'
const requiredHint = ' *'

const FormItemCheckbox = (props) => {
  const { item, onFormItemChange, onFormItemLoad, onGroupValidation, groupedState, revealState } = props
  const { register, watch, errors, formState, trigger } = useFormContext()
  const userContext = useContext(UserContext)

  // Translations
  const { t } = useTranslation(
    userContext?.userSession?.sportType?.slug ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_NAMESPACE ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_SPORT
  )
  const requiredErrorMessage = t('FORMITEM__ERROR_REQUIRED')

  // States
  const [isRequired, setIsRequired] = useState({ required: false })
  const [isGrouped, setIsGrouped] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)

  const itemRef = useRef()
  const itemProps = item
  const itemValue = watch(item.name)
  const itemInputRef = register(item.name, isRequired)

  useEffect(() => {
    if (!item.revealed && item.required) setIsRequired({ required: requiredErrorMessage })
    if (!item.revealed && item.requiredGroup)
      setIsRequired({ required: false, validate: onGroupValidation({ itemProps }) })
  }, [])

  useEffect(() => {
    if (itemValue === undefined || itemValue === null) return false
    if (isLoaded) itemProps.checked = itemValue
    if (!isLoaded && onFormItemLoad) onFormItemLoad({ itemProps, itemRef, itemValue })
    else if (onFormItemLoad) onFormItemChange({ itemProps, itemRef, itemValue })
    if (isLoaded) trigger()
    if (!isLoaded) setIsLoaded(true)
  }, [itemValue])

  useEffect(() => {
    if (item.revealed && item.required) setIsRequired({ required: revealState ? requiredErrorMessage : false })
    if (item.revealed && item.requiredGroup)
      setIsRequired({ required: false, validate: onGroupValidation({ itemProps }) })
    trigger()
  }, [revealState])

  useEffect(() => {
    if (item?.group && groupedState) {
      const itemLabel = item?.group?.label
      const groupedItem = groupedState.filter((str) => {
        return str.label.indexOf(itemLabel) >= 0
      })
      setIsGrouped(groupedItem[0].active)
    }
  }, [groupedState])

  return (
    <div
      ref={itemRef}
      className={`FormItem FormItem--${item.type}${item.modifier ? ` FormItem--${item.modifier}` : ''}${
        revealState ? revealClass : ''
      }${!isGrouped ? '' : activeClass}`}
      data-reveal-target={item.revealed}
    >
      <label className="FormItem-checkboxLabel" htmlFor={item.name}>
        <input
          type="checkbox"
          defaultChecked={item.checked}
          id={item.name}
          name={item.name}
          className={`FormItem-input FormItem-input--${item.type}`}
          onChange={(e) => {
            itemInputRef.onChange(e)
          }}
          ref={itemInputRef.ref}
        />
        <div className="FormItem-checkboxIndicator">
          <Svg id="icon-check" modifiers={['FormItem-checkboxIcon']} />
        </div>
        {item.richText ? (
          <RichTextBlock
            content={!item.richText.data && item.richText ? (!item.transl ? item.richText : t(item.richText)) : ''}
            data={
              item.richText.data
                ? item.richText.data && item.richText.transl
                  ? convertDataToTranslation(item.richText.data, t)
                  : item.richText.data
                : []
            }
            modifiers={['RichTextBlock--modalContent']}
          />
        ) : (
          <>{`${item.transl ? t(item.transl) : item.label}`}</>
        )}
        {`${item.required ? requiredHint : ''}`}
      </label>
      {formState.isSubmitted && <ErrorMessage errors={errors} name={item.name} as={<FormError />} />}
    </div>
  )
}

FormItemCheckbox.propTypes = {
  item: PropTypes.object,
  onFormItemChange: PropTypes.func,
  onFormItemLoad: PropTypes.func,
  onGroupValidation: PropTypes.func,
  revealState: PropTypes.any,
  groupedState: PropTypes.any
}

export default FormItemCheckbox
